import { useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { Task } from '../page';

interface Comment {
  id: string;
  task_id: string;
  user_id: string | null;
  content: string;
  created_at: string;
  _user_name?: string;
  _is_own?: boolean;
  _pending?: boolean;
}

interface TaskCommentsProps {
  task: Task;
  userId: string;
  userName: string;
  onClose: () => void;
}

const PRIORITY_LABEL: Record<string, string> = {
  critical: 'Crítica', high: 'Alta', medium: 'Média', low: 'Baixa',
};

export default function TaskComments({ task, userId, userName, onClose }: TaskCommentsProps) {
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [sendError, setSendError] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const userNamesRef = useRef<Record<string, string>>({});

  const fetchUserNames = useCallback(async (userIds: string[]) => {
    const missing = userIds.filter(id => !userNamesRef.current[id]);
    if (!missing.length) return;
    const { data } = await supabase.from('profiles').select('id, full_name').in('id', missing);
    (data || []).forEach((u: { id: string; full_name: string }) => {
      userNamesRef.current[u.id] = u.full_name || 'Usuário';
    });
  }, []);

  const mapComment = useCallback((c: Comment): Comment => ({
    ...c,
    _user_name: c.user_id ? (userNamesRef.current[c.user_id] || 'Usuário') : 'Usuário',
    _is_own: c.user_id === userId,
  }), [userId]);

  const loadComments = useCallback(async () => {
    const { data, error } = await supabase
      .from('task_comments')
      .select('*')
      .eq('task_id', task.id)
      .order('created_at');

    if (error) {
      console.error('Erro ao carregar comentários:', error);
      setLoading(false);
      return;
    }

    const rows = data || [];
    const userIds = [...new Set(rows.map((c: Comment) => c.user_id).filter(Boolean))] as string[];
    await fetchUserNames(userIds);
    setComments(rows.map(mapComment));
    setLoading(false);
  }, [task.id, fetchUserNames, mapComment]);

  useEffect(() => { loadComments(); }, [loadComments]);

  // Realtime — sync messages from other users
  useEffect(() => {
    const channel = supabase
      .channel(`task-comments-${task.id}`)
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'task_comments', filter: `task_id=eq.${task.id}` },
        async (payload) => {
          const newRow = payload.new as Comment;
          // Skip if it's our own message (already added optimistically)
          if (newRow.user_id === userId) return;
          const userIds = newRow.user_id ? [newRow.user_id] : [];
          await fetchUserNames(userIds);
          setComments(prev => {
            // Avoid duplicates
            if (prev.some(c => c.id === newRow.id)) return prev;
            return [...prev, mapComment(newRow)];
          });
        }
      )
      .on(
        'postgres_changes',
        { event: 'DELETE', schema: 'public', table: 'task_comments', filter: `task_id=eq.${task.id}` },
        (payload) => {
          setComments(prev => prev.filter(c => c.id !== (payload.old as Comment).id));
        }
      )
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [task.id, userId, fetchUserNames, mapComment]);

  // Scroll to bottom
  useEffect(() => {
    if (!loading) {
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  }, [comments.length, loading]);

  // Send notifications to all other project participants
  const sendNotifications = useCallback(async (commentText: string) => {
    try {
      // 1. Get project_id from task
      const projectId = task.project_id;

      // 2. Collect all user_ids that should be notified (everyone except sender)
      const recipientSet = new Set<string>();

      // 1. project_members
      const { data: members } = await supabase
        .from('project_members')
        .select('user_id')
        .eq('project_id', projectId);
      (members || []).forEach((m: { user_id: string }) => {
        if (m.user_id !== userId) recipientSet.add(m.user_id);
      });

      // 2. All admin users (RLS allows any authenticated user to see admin profiles)
      const { data: admins } = await supabase
        .from('profiles')
        .select('id')
        .eq('role', 'admin');
      (admins || []).forEach((a: { id: string }) => {
        if (a.id !== userId) recipientSet.add(a.id);
      });

      // 3. Client users linked to this project
      const { data: proj } = await supabase
        .from('projects')
        .select('client_id')
        .eq('id', projectId)
        .maybeSingle();
      if (proj?.client_id) {
        const { data: clientUsers } = await supabase
          .from('profiles')
          .select('id')
          .eq('client_id', proj.client_id);
        (clientUsers || []).forEach((u: { id: string }) => {
          if (u.id !== userId) recipientSet.add(u.id);
        });
      }

      if (recipientSet.size === 0) return;

      // 3. Insert one notification per recipient
      const preview = commentText.length > 80 ? commentText.slice(0, 80) + '…' : commentText;
      const inserts = Array.from(recipientSet).map(uid => ({
        user_id: uid,
        type: 'comment',
        title: `${userName} comentou em "${task.title}"`,
        body: preview,
        task_id: task.id,
        project_id: projectId,
        from_user_id: userId,
        read: false,
      }));

      await supabase.from('notifications').insert(inserts);
    } catch (err) {
      console.error('Erro ao enviar notificações:', err);
    }
  }, [task, userId, userName]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const text = newComment.trim();
    if (!text || saving) return;

    setSendError('');
    setSaving(true);

    // Optimistic update — show message immediately
    const tempId = `temp-${Date.now()}`;
    const optimistic: Comment = {
      id: tempId,
      task_id: task.id,
      user_id: userId,
      content: text,
      created_at: new Date().toISOString(),
      _user_name: userName,
      _is_own: true,
      _pending: true,
    };
    setComments(prev => [...prev, optimistic]);
    setNewComment('');

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }

    const { data, error } = await supabase
      .from('task_comments')
      .insert({ task_id: task.id, user_id: userId, content: text })
      .select()
      .maybeSingle();

    if (error) {
      console.error('Erro ao enviar comentário:', error);
      // Remove optimistic message and show error
      setComments(prev => prev.filter(c => c.id !== tempId));
      setNewComment(text);
      setSendError('Não foi possível enviar. Tente novamente.');
    } else if (data) {
      // Replace optimistic with real message
      setComments(prev => prev.map(c =>
        c.id === tempId ? { ...mapComment(data as Comment), _pending: false } : c
      ));
      // Fire notifications after confirmed insert
      sendNotifications(text);
    }

    setSaving(false);
    textareaRef.current?.focus();
  };

  const handleDelete = async (commentId: string) => {
    // Optimistic remove
    setComments(prev => prev.filter(c => c.id !== commentId));
    setDeleteConfirm(null);
    await supabase.from('task_comments').delete().eq('id', commentId);
  };

  const fmtTime = (d: string) => {
    const date = new Date(d);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    if (isToday) return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    return (
      date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }) +
      ' ' +
      date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    );
  };

  const initials = (name: string) =>
    name.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

  // Group consecutive messages by same user (within 5 min)
  const grouped = comments.map((c, i) => {
    const prev = comments[i - 1];
    const isSameUser = prev?.user_id === c.user_id;
    const timeDiff = prev
      ? (new Date(c.created_at).getTime() - new Date(prev.created_at).getTime()) / 60000
      : 999;
    const isGrouped = isSameUser && timeDiff < 5;
    return { comment: c, showMeta: !isGrouped };
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-xl w-full max-w-lg h-[85vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-100 shrink-0">
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-cyan-50 shrink-0">
              <i className="ri-chat-3-line text-cyan-500 text-base"></i>
            </div>
            <div className="min-w-0">
              <h2 className="text-slate-800 font-semibold text-sm leading-tight truncate">{task.title}</h2>
              <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                <span className={`inline-flex items-center gap-1 text-xs font-medium ${
                  task.type === 'bug' ? 'text-red-500' : 'text-violet-500'
                }`}>
                  <i className={task.type === 'bug' ? 'ri-bug-line' : 'ri-add-circle-line'}></i>
                  {task.type === 'bug' ? 'Bug' : 'Demanda'}
                </span>
                <span className="text-slate-300 text-xs">·</span>
                <span className="text-slate-400 text-xs">{PRIORITY_LABEL[task.priority] || task.priority}</span>
                <span className="text-slate-300 text-xs">·</span>
                <span className="text-slate-400 text-xs">
                  {comments.filter(c => !c._pending).length} mensagem{comments.filter(c => !c._pending).length !== 1 ? 's' : ''}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer shrink-0 ml-2"
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-4 py-4 bg-slate-50/60">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <i className="ri-loader-4-line animate-spin text-cyan-500 text-2xl"></i>
            </div>
          ) : comments.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-slate-400">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-white border border-slate-200">
                <i className="ri-chat-3-line text-2xl text-slate-300"></i>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-slate-500">Nenhuma mensagem ainda</p>
                <p className="text-xs text-slate-400 mt-0.5">Inicie a conversa sobre este item</p>
              </div>
            </div>
          ) : (
            <div className="space-y-0.5">
              {grouped.map(({ comment: c, showMeta }) => {
                const isOwn = c._is_own;
                return (
                  <div
                    key={c.id}
                    className={`flex items-end gap-2 ${isOwn ? 'flex-row-reverse' : 'flex-row'} ${showMeta ? 'mt-4' : 'mt-0.5'}`}
                  >
                    {/* Avatar */}
                    <div className={`w-7 h-7 shrink-0 rounded-full text-xs font-bold flex items-center justify-center ${
                      showMeta ? 'opacity-100' : 'opacity-0 pointer-events-none'
                    } ${isOwn ? 'bg-cyan-500 text-white' : 'bg-slate-200 text-slate-600'}`}>
                      {initials(c._user_name || 'U')}
                    </div>

                    {/* Bubble */}
                    <div className={`group flex flex-col max-w-[72%] ${isOwn ? 'items-end' : 'items-start'}`}>
                      {showMeta && !isOwn && (
                        <span className="text-xs text-slate-400 font-medium mb-1 ml-1">{c._user_name}</span>
                      )}
                      <div className={`relative px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${
                        isOwn
                          ? `bg-cyan-500 text-white rounded-br-sm ${c._pending ? 'opacity-70' : ''}`
                          : 'bg-white text-slate-700 border border-slate-200 rounded-bl-sm'
                      }`}>
                        <p className="whitespace-pre-wrap break-words">{c.content}</p>
                        <div className={`flex items-center justify-end gap-1 mt-1 ${isOwn ? 'text-cyan-100' : 'text-slate-400'}`}>
                          <span className="text-xs">{fmtTime(c.created_at)}</span>
                          {isOwn && (
                            c._pending
                              ? <i className="ri-time-line text-xs opacity-70"></i>
                              : <i className="ri-check-double-line text-xs"></i>
                          )}
                        </div>
                      </div>

                      {/* Delete (hover, own only) */}
                      {isOwn && !c._pending && (
                        <button
                          onClick={() => setDeleteConfirm(c.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity mt-1 text-xs text-slate-400 hover:text-red-500 cursor-pointer flex items-center gap-1"
                        >
                          <i className="ri-delete-bin-line text-xs"></i>
                          Apagar
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        {/* Error */}
        {sendError && (
          <div className="px-4 py-2 bg-red-50 border-t border-red-100 shrink-0">
            <p className="text-xs text-red-600 flex items-center gap-1.5">
              <i className="ri-error-warning-line"></i>
              {sendError}
            </p>
          </div>
        )}

        {/* Input */}
        <div className="px-4 py-3 border-t border-slate-100 bg-white shrink-0">
          <form onSubmit={handleSubmit} className="flex items-end gap-2">
            <div className="w-7 h-7 flex items-center justify-center rounded-full bg-cyan-500 text-white text-xs font-bold shrink-0 mb-1">
              {initials(userName)}
            </div>
            <div className="flex-1">
              <textarea
                ref={textareaRef}
                value={newComment}
                onChange={e => {
                  setNewComment(e.target.value);
                  setSendError('');
                }}
                placeholder="Escreva uma mensagem..."
                rows={1}
                maxLength={500}
                className="w-full px-4 py-2.5 text-sm border border-slate-200 rounded-2xl text-slate-700 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-cyan-300 resize-none bg-slate-50 focus:bg-white transition-colors"
                style={{ minHeight: '42px', maxHeight: '120px' }}
                onInput={e => {
                  const el = e.currentTarget;
                  el.style.height = 'auto';
                  el.style.height = Math.min(el.scrollHeight, 120) + 'px';
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    handleSubmit(e);
                  }
                }}
              />
            </div>
            <button
              type="submit"
              disabled={saving || !newComment.trim()}
              className="w-9 h-9 flex items-center justify-center rounded-full bg-cyan-500 hover:bg-cyan-600 disabled:bg-slate-200 disabled:text-slate-400 text-white transition-colors cursor-pointer shrink-0 mb-0.5"
            >
              {saving
                ? <i className="ri-loader-4-line animate-spin text-sm"></i>
                : <i className="ri-send-plane-fill text-sm"></i>
              }
            </button>
          </form>
          <p className="text-xs text-slate-400 mt-1.5 ml-9">Enter para enviar · Shift+Enter para nova linha</p>
        </div>
      </div>

      {/* Delete confirm */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl p-5 w-full max-w-xs">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-red-50 mb-3">
              <i className="ri-delete-bin-line text-red-500 text-lg"></i>
            </div>
            <h3 className="text-slate-800 font-semibold text-sm mb-1">Apagar mensagem?</h3>
            <p className="text-slate-500 text-xs mb-4">Esta ação não pode ser desfeita.</p>
            <div className="flex gap-2">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Cancelar
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="flex-1 px-3 py-2 text-sm bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                Apagar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
